import { useCallback, useRef } from 'react';

import { noise2D } from '../canvas/noise';
import { drawSymbolShape } from '../canvas/symbol-renderer';
import { BUFFER_SIZE } from '../canvas/types';

import { MAP_SYMBOLS } from '@/lib/map-assets';

// Terrain type IDs for stampMask buffer
export const STAMP_NONE = 0;
export const STAMP_MOUNTAIN = 1;
export const STAMP_FOREST = 2;
export const STAMP_DUNE = 3;
export const STAMP_SWAMP = 4;

// Helper to mark mask
const markMask = (x: number, y: number, radius: number, mask: Uint8Array) => {
    const r = Math.ceil(radius);
    const r2 = r * r;
    const startX = Math.max(0, Math.floor(x - r));
    const endX = Math.min(BUFFER_SIZE - 1, Math.ceil(x + r));
    const startY = Math.max(0, Math.floor(y - r));
    const endY = Math.min(BUFFER_SIZE - 1, Math.ceil(y + r));

    for (let py = startY; py <= endY; py++) {
        for (let px = startX; px <= endX; px++) {
            const dx = px - x;
            const dy = py - y;
            if (dx * dx + dy * dy <= r2) {
                mask[py * BUFFER_SIZE + px] = 1;
            }
        }
    }
};

// Helper to clear mask
const clearMask = (x: number, y: number, radius: number, mask: Uint8Array) => {
    const r = Math.ceil(radius);
    const r2 = r * r;
    const startX = Math.max(0, Math.floor(x - r));
    const endX = Math.min(BUFFER_SIZE - 1, Math.ceil(x + r));
    const startY = Math.max(0, Math.floor(y - r));
    const endY = Math.min(BUFFER_SIZE - 1, Math.ceil(y + r));

    for (let py = startY; py <= endY; py++) {
        for (let px = startX; px <= endX; px++) {
            const dx = px - x;
            const dy = py - y;
            if (dx * dx + dy * dy <= r2) {
                mask[py * BUFFER_SIZE + px] = 0;
            }
        }
    }
};


interface DrawOptions {
    activeSection: string;
    activeLandTool: string;
    tool: string;
    brush: string;
    brushSize: number;
    paintOpacity: number;
    activeSymbol?: string;
    activeSymbols?: string[];
    processedSheets: Record<string, HTMLCanvasElement | HTMLImageElement>;
    stampType?: 'mountain' | 'forest' | 'dune' | 'swamp';
    stampDensity?: number;
    symbolMode?: 'single' | 'brush' | 'eraser' | 'select';
    isNatural?: boolean;
    symbolSize?: number;
    roughness: number;
}


export function useMapDrawing(
    landRef: React.RefObject<HTMLCanvasElement | null>,
    colorRef: React.RefObject<HTMLCanvasElement | null>,
    symbolsRef: React.RefObject<HTMLCanvasElement | null>,
    landMaskRef: React.RefObject<Uint8Array | null>,
    stampMaskRef: React.RefObject<Uint8Array | null>
) {
    const lastPos = useRef<{ x: number, y: number } | null>(null);
    const distAcc = useRef<number>(0);
    const symbolCycleIdx = useRef<number>(0);

    const applyOrganicBrush = useCallback((
        x: number, y: number,
        isRaise: boolean,
        options: DrawOptions,
        dirtyRect: { minX: number, minY: number, maxX: number, maxY: number }
    ) => {
        const mask = landMaskRef.current;
        if (!mask) return;

        const { brushSize, roughness } = options;
        const radius = brushSize * 10;
        const r2 = radius * radius;

        // Tunables scaled by roughness (roughness 0 yields a perfect circle)
        const freq = 0.05;
        const amp = (roughness / 5) * 0.4;
        const warpFreq = 0.02;
        const warpAmp = (roughness / 5) * 10;

        const startX = Math.max(0, Math.floor(x - radius - 10));
        const endX = Math.min(BUFFER_SIZE - 1, Math.ceil(x + radius + 10));
        const startY = Math.max(0, Math.floor(y - radius - 10));
        const endY = Math.min(BUFFER_SIZE - 1, Math.ceil(y + radius + 10));

        for (let py = startY; py <= endY; py++) {
            for (let px = startX; px <= endX; px++) {
                const dx = px - x;
                const dy = py - y;
                const dist2 = dx * dx + dy * dy;
                if (dist2 > r2 * 1.5) continue; // Early out

                const dist = Math.sqrt(dist2);
                const t = dist / radius;

                // Organic Noise Boundary
                // edgeMask: irregularity mainly near the edge (0.6 - 1.0)
                const edgeMask = Math.max(0, Math.min(1, (t - 0.6) / 0.4));

                // Domain Warp
                const wx = noise2D(px * warpFreq, py * warpFreq) * warpAmp;
                const wy = noise2D((px + 500) * warpFreq, (py + 500) * warpFreq) * warpAmp;

                const n = noise2D((px + wx) * freq, (py + wy) * freq);
                const val = t + n * amp * edgeMask;

                if (val < 1.0) {
                    const idx = py * BUFFER_SIZE + px;
                    mask[idx] = isRaise ? 255 : 0;

                    // Update dirty rect
                    dirtyRect.minX = Math.min(dirtyRect.minX, px);
                    dirtyRect.minY = Math.min(dirtyRect.minY, py);
                    dirtyRect.maxX = Math.max(dirtyRect.maxX, px);
                    dirtyRect.maxY = Math.max(dirtyRect.maxY, py);
                }
            }
        }
    }, [landMaskRef]);

    const handlePaint = useCallback((
        x: number, y: number,
        lx: number, ly: number,
        options: DrawOptions,
        dirtyRect: { minX: number, minY: number, maxX: number, maxY: number }
    ) => {
        const { tool, activeSection, activeLandTool, brush, brushSize } = options;
        const isEraser = tool === 'eraser' && activeSection !== 'land';

        if (activeLandTool === 'raise' || activeLandTool === 'sink' || isEraser) {
            const isRaise = activeLandTool === 'raise' && !isEraser;

            // Interpolate along the segment
            const dist = Math.sqrt((x - lx) ** 2 + (y - ly) ** 2);
            const steps = Math.max(1, Math.floor(dist / (brushSize * 2))); // Step every half-radius approx

            for (let i = 0; i <= steps; i++) {
                const interpX = lx + (x - lx) * (i / steps);
                const interpY = ly + (y - ly) * (i / steps);
                applyOrganicBrush(interpX, interpY, isRaise, options, dirtyRect);
            }
        }

        if (activeLandTool === 'paint') {
            const ctx = colorRef.current?.getContext('2d');
            if (!ctx) return;
            const colors: Record<string, string> = {
                land: '#c9b88a', mountain: '#5a6575', forest: '#3b6637',
                path: '#8b5a2b', desert: '#e29c3a', 'farm land': '#8eb358'
            };
            const color = colors[brush] || colors.land;
            const radius = brushSize * 10;

            ctx.save();
            if (brush === 'land') {
                ctx.globalCompositeOperation = 'destination-out';
                ctx.fillStyle = 'rgba(0,0,0,1)';
            } else {
                ctx.globalCompositeOperation = 'source-over';
                ctx.fillStyle = color;
            }
            ctx.globalAlpha = 1.0;

            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();

            // For paint tool, we just mark the area dirty in world coords
            dirtyRect.minX = Math.min(dirtyRect.minX, x - radius);
            dirtyRect.maxX = Math.max(dirtyRect.maxX, x + radius);
            dirtyRect.minY = Math.min(dirtyRect.minY, y - radius);
            dirtyRect.maxY = Math.max(dirtyRect.maxY, y + radius);
        }
    }, [landRef, colorRef, applyOrganicBrush]);

    const handleSymbol = useCallback((x: number, y: number, isMove: boolean, options: DrawOptions) => {
        const { paintOpacity, tool, activeSymbol, brushSize, processedSheets, symbolSize = 20, symbolMode } = options;
        const ctx = symbolsRef.current?.getContext('2d');
        const stampMask = stampMaskRef.current;
        if (!ctx) return;

        ctx.globalAlpha = paintOpacity;
        const isEraser = tool === 'eraser' || symbolMode === 'eraser';

        if (isEraser) {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.beginPath();
            ctx.arc(x, y, brushSize * 5, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalCompositeOperation = 'source-over';

            // Clear mask where erased
            if (stampMask) {
                clearMask(x, y, brushSize * 5, stampMask);
            }
            return;
        }

        const isNatural = options.isNatural || false;

        if (isNatural) {
            // Brush mode: scatter symbols naturally within brush area
            const density = options.stampDensity || 0.3;
            const symbolsToUse = options.activeSymbols && options.activeSymbols.length > 0
                ? options.activeSymbols
                : [activeSymbol].filter(Boolean) as string[];

            if (symbolsToUse.length === 0) return;

            // Distance-based placement to control speed via density
            // spacing: Bigger brush -> more gap (slower). More density -> less gap (quicker).
            const spacing = Math.max(2, (brushSize * 0.5) * (1.2 - density));

            if (!isMove) {
                distAcc.current = spacing; // Trigger immediately on first click
            } else if (lastPos.current) {
                const d = Math.sqrt((x - lastPos.current.x) ** 2 + (y - lastPos.current.y) ** 2);
                distAcc.current += d;
            }

            if (distAcc.current >= spacing) {
                distAcc.current = 0;

                // Number of symbols per point
                const count = Math.max(1, Math.floor(density * 8));
                const batch: { x: number, y: number, size: number, id: string }[] = [];

                for (let i = 0; i < count; i++) {
                    const offset = (brushSize * 0.7); // Keep symbols within the brush radius
                    const rx = x + (Math.random() - 0.5) * offset * 2;
                    const ry = y + (Math.random() - 0.5) * offset * 2;
                    const rSize = symbolSize * (0.8 + Math.random() * 0.4);

                    const sId = symbolsToUse[symbolCycleIdx.current % symbolsToUse.length];
                    symbolCycleIdx.current++;

                    batch.push({ x: rx, y: ry, size: rSize, id: sId });
                }

                batch.sort((a, b) => a.y - b.y);
                batch.forEach(p => {
                    drawSymbolShape(ctx, p.id, p.x, p.y, p.size, processedSheets);
                    if (stampMask) markMask(p.x, p.y, p.size * 0.5, stampMask);
                });
            }
        } else {
            // Single mode: place one symbol per click
            if (!isMove && activeSymbol) {
                drawSymbolShape(ctx, activeSymbol, x, y, symbolSize, processedSheets);
                if (stampMask) markMask(x, y, symbolSize * 0.5, stampMask);
            }
        }
    }, [symbolsRef]);

    const handleStamp = useCallback((
        x: number, y: number,
        lx: number, ly: number,
        options: DrawOptions,
        dirtyRect: { minX: number, minY: number, maxX: number, maxY: number }
    ) => {
        const { stampType, brushSize, stampDensity = 0.3, processedSheets } = options;
        if (!stampType) return;

        const landMask = landMaskRef.current;
        const stampMask = stampMaskRef.current;
        if (!landMask || !stampMask) return;

        // Map terrain type to stamp ID and category/subcategory
        const terrainMap: Record<string, { id: number, category: string, subcategory?: string }> = {
            mountain: { id: STAMP_MOUNTAIN, category: 'mountains' },
            forest: { id: STAMP_FOREST, category: 'trees' },
            dune: { id: STAMP_DUNE, category: 'terrain', subcategory: 'Desert' },
            swamp: { id: STAMP_SWAMP, category: 'terrain', subcategory: 'Swamp' }
        };

        const terrain = terrainMap[stampType];
        if (!terrain) return;

        const radius = brushSize * 10;

        // Get available variations for this terrain type
        const variations = MAP_SYMBOLS.filter(s =>
            s.category === terrain.category &&
            (!terrain.subcategory || s.subcategory === terrain.subcategory) &&
            s.src
        );

        if (variations.length === 0) return;

        // Interpolate along the segment for smooth stamping
        const dist = Math.sqrt((x - lx) ** 2 + (y - ly) ** 2);
        const steps = Math.max(1, Math.floor(dist / (brushSize * 4)));

        // Collect all stamps to be placed in this stroke segment
        const batch: { x: number, y: number, size: number, symbolId: string }[] = [];

        for (let i = 0; i <= steps; i++) {
            const interpX = lx + (x - lx) * (i / steps);
            const interpY = ly + (y - ly) * (i / steps);

            // Increase probability of placement to make density feel more "on"
            if (Math.random() > (stampDensity * 1.5)) continue;

            // Random offset within brush area
            const angle = Math.random() * Math.PI * 2;
            const offsetDist = Math.random() * radius * 0.7;
            const px = Math.floor(interpX + Math.cos(angle) * offsetDist);
            const py = Math.floor(interpY + Math.sin(angle) * offsetDist);

            if (px < 0 || px >= BUFFER_SIZE || py < 0 || py >= BUFFER_SIZE) continue;

            const idx = py * BUFFER_SIZE + px;

            // Check if position is LAND (not water)
            const isLand = landMask[idx] > 128;
            if (!isLand) continue;

            // Select random variation from manifest
            const symbol = variations[Math.floor(Math.random() * variations.length)];

            // Random size variation (0.8x to 1.2x)
            const sizeVariation = 0.8 + Math.random() * 0.4;
            const stampSize = brushSize * 4 * sizeVariation;

            batch.push({ x: px, y: py, size: stampSize, symbolId: symbol.id });
        }

        // Y-Sort the batch for depth layering
        batch.sort((a, b) => a.y - b.y);

        const ctx = symbolsRef.current?.getContext('2d');
        if (!ctx) return;

        batch.forEach(p => {
            // Draw the sprite
            drawSymbolShape(ctx, p.symbolId, p.x, p.y, p.size, processedSheets);

            // Mark a region around the sprite as stamped for metadata (id)
            const markRadius = p.size / 2;
            const mr2 = markRadius * markRadius;
            for (let my = Math.max(0, p.y - markRadius); my <= Math.min(BUFFER_SIZE - 1, p.y + markRadius); my++) {
                for (let mx = Math.max(0, p.x - markRadius); mx <= Math.min(BUFFER_SIZE - 1, p.x + markRadius); mx++) {
                    const dx = mx - p.x;
                    const dy = my - p.y;
                    if (dx * dx + dy * dy <= mr2) {
                        const markIdx = Math.floor(my) * BUFFER_SIZE + Math.floor(mx);
                        stampMask[markIdx] = terrain.id;
                    }
                }
            }

            // Update dirty rect
            dirtyRect.minX = Math.min(dirtyRect.minX, p.x - markRadius);
            dirtyRect.maxX = Math.max(dirtyRect.maxX, p.x + markRadius);
            dirtyRect.minY = Math.min(dirtyRect.minY, p.y - markRadius);
            dirtyRect.maxY = Math.max(dirtyRect.maxY, p.y + markRadius);
        });
    }, [landMaskRef, stampMaskRef, symbolsRef]);

    return { lastPos, handlePaint, handleSymbol, handleStamp };
}
